import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Coins,
  Pickaxe,
  Share2,
  Tv,
  CheckCircle2,
  Clock,
  ShieldAlert,
  Loader2,
  Star,
  FileText,
  Calendar,
} from 'lucide-react';
import { adminApi } from '../services/api.js';
import type { AdminUserDetailResponse } from '@nova-mine/shared';

interface UserDetailModalProps {
  userId: string | null;
  onClose: () => void;
  onOpenAdjust: (user: any) => void;
  onToggleBan: (user: any) => void;
}

type DetailTab = 'overview' | 'miners' | 'ledger' | 'referrals' | 'tasks' | 'ads' | 'stars' | 'audit';

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  userId,
  onClose,
  onOpenAdjust,
  onToggleBan,
}) => {
  const [detail, setDetail] = useState<AdminUserDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');

  useEffect(() => {
    if (!userId) return;
    async function fetchDetail() {
      try {
        setIsLoading(true);
        setError(null);
        const res = await adminApi.getUserDetail(userId!);
        setDetail(res);
      } catch (err: unknown) {
        console.error('[Fetch User Detail Error]', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch user details');
      } finally {
        setIsLoading(false);
      }
    }
    fetchDetail();
  }, [userId]);

  if (!userId) return null;

  const tabs: Array<{ id: DetailTab; label: string; count?: number }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'miners', label: 'Miners', count: detail?.miners.length },
    { id: 'ledger', label: 'Coin Ledger', count: detail?.ledger.length },
    { id: 'referrals', label: 'Referrals', count: detail?.referrals.length },
    { id: 'tasks', label: 'Tasks', count: detail?.website_tasks.length },
    { id: 'ads', label: 'AdsGram', count: detail?.ad_attempts.length },
    { id: 'stars', label: 'Stars Orders', count: detail?.stars_orders.length },
    { id: 'audit', label: 'Audit Trail', count: detail?.audit_logs.length },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="w-full max-w-4xl max-h-[90vh] glass-panel-glow rounded-3xl border border-white/15 flex flex-col shadow-2xl overflow-hidden bg-space-950">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white flex items-center space-x-2">
                <span>{detail ? detail.user.first_name : 'Loading User...'}</span>
                {detail?.user.is_banned && (
                  <span className="text-[10px] uppercase font-bold text-red-400 bg-red-500/10 border border-red-500/30 px-2 py-0.5 rounded-full">
                    BANNED
                  </span>
                )}
              </h3>
              <p className="text-xs text-gray-400 font-mono">
                Telegram ID: {detail?.user.telegram_id || '...'} • Username: {detail?.user.username ? `@${detail.user.username}` : 'None'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {detail && (
              <>
                <button
                  onClick={() => onOpenAdjust(detail.user)}
                  className="px-3 py-1.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-nova-gold text-xs font-bold hover:bg-amber-500/20 transition-colors"
                >
                  Adjust Balance
                </button>
                <button
                  onClick={() => onToggleBan(detail.user)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                    detail.user.is_banned
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      : 'bg-red-500/20 text-red-300 border border-red-500/30'
                  }`}
                >
                  {detail.user.is_banned ? 'Unban User' : 'Ban User'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-space-900 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 pt-3 border-b border-white/5 flex space-x-2 overflow-x-auto">
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-2.5 px-3 text-xs font-bold border-b-2 transition-all flex items-center space-x-1.5 whitespace-nowrap ${
                  active
                    ? 'border-purple-400 text-purple-300'
                    : 'border-transparent text-gray-400 hover:text-gray-200'
                }`}
              >
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-space-850 text-gray-400">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="py-16 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400 mx-auto mb-2" />
              <p className="text-xs text-gray-400">Loading comprehensive account records...</p>
            </div>
          ) : error ? (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
              {error}
            </div>
          ) : detail ? (
            <>
              {/* TAB 1: OVERVIEW */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Metric Highlights */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-4 rounded-2xl bg-space-900 border border-white/5">
                      <span className="text-xs text-gray-400 block">Current Balance</span>
                      <span className="text-xl font-bold font-mono text-nova-gold mt-1 block">
                        {detail.user.balance.toLocaleString()} NOVA
                      </span>
                    </div>
                    <div className="p-4 rounded-2xl bg-space-900 border border-white/5">
                      <span className="text-xs text-gray-400 block">Lifetime Earnings</span>
                      <span className="text-xl font-bold font-mono text-emerald-400 mt-1 block">
                        {detail.user.lifetime_earnings.toLocaleString()} NOVA
                      </span>
                    </div>
                    <div className="p-4 rounded-2xl bg-space-900 border border-white/5">
                      <span className="text-xs text-gray-400 block">Active Stars Rigs</span>
                      <span className="text-xl font-bold font-mono text-white mt-1 block">
                        {detail.user.stars_miners_count} units
                      </span>
                    </div>
                  </div>

                  {/* Profile Attributes */}
                  <div className="rounded-2xl bg-space-900/60 border border-white/5 p-4 space-y-2.5 text-xs">
                    <div className="flex justify-between py-1 border-b border-white/5">
                      <span className="text-gray-400">User ID (Internal):</span>
                      <span className="font-mono text-gray-200">{detail.user.id}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-white/5">
                      <span className="text-gray-400">Referral Code:</span>
                      <span className="font-mono font-bold text-nova-cyan">{detail.user.referral_code}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-white/5">
                      <span className="text-gray-400">Attributed Referrer:</span>
                      <span className="font-mono text-gray-300">
                        {detail.user.referrer_telegram_id ? `Telegram ID: ${detail.user.referrer_telegram_id}` : 'Direct (Organic)'}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-white/5">
                      <span className="text-gray-400">Registered:</span>
                      <span className="text-gray-300">{new Date(detail.user.created_at).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-400">Auto Miner Status:</span>
                      <span className={`font-bold ${detail.user.auto_miner_unlocked ? 'text-emerald-400' : 'text-gray-400'}`}>
                        {detail.user.auto_miner_unlocked ? 'Unlocked & Active' : 'Locked (Requires 3 Tasks)'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: MINERS */}
              {activeTab === 'miners' && (
                <div className="space-y-3">
                  {detail.miners.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No miners found for this user.</p>
                  ) : (
                    detail.miners.map((m) => (
                      <div key={m.id} className="p-4 rounded-2xl bg-space-900 border border-white/5 flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-bold text-white uppercase">{m.miner_type} Miner</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              m.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {m.status.toUpperCase()}
                            </span>
                            {m.units > 1 && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-nova-gold">
                                {m.units}x Units
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-gray-400 mt-1 font-mono">
                            ID: {m.id} • Rate: {m.daily_rate.toLocaleString()} / 24h
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-emerald-400 font-bold font-mono block">
                            +{m.accrued_coins.toLocaleString()} accrued
                          </span>
                          <span className="text-[10px] text-gray-500 block">
                            {m.total_claimed.toLocaleString()} claimed total
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* TAB 3: LEDGER */}
              {activeTab === 'ledger' && (
                <div className="space-y-2">
                  {detail.ledger.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No ledger transactions found.</p>
                  ) : (
                    detail.ledger.map((l) => (
                      <div key={l.id} className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between text-xs">
                        <div>
                          <span className="font-bold text-gray-200 block">{l.transaction_type}</span>
                          <span className="text-[10px] text-gray-500">
                            {new Date(l.created_at).toLocaleString()} • Tx: {l.id}
                          </span>
                          {Boolean(l.metadata?.reason) && (
                            <span className="block text-[11px] text-purple-300 mt-0.5">
                              Reason: {String(l.metadata?.reason)}
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`font-mono font-bold block ${l.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {l.amount >= 0 ? `+${l.amount.toLocaleString()}` : l.amount.toLocaleString()}
                          </span>
                          <span className="text-[10px] text-gray-400 font-mono">
                            Bal: {l.balance_after.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* TAB 4: REFERRALS */}
              {activeTab === 'referrals' && (
                <div className="space-y-2">
                  {detail.referrals.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No invited referrals yet.</p>
                  ) : (
                    detail.referrals.map((r) => (
                      <div key={r.id} className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between text-xs">
                        <div>
                          <span className="font-bold text-white">{r.referee_name}</span>
                          <span className="text-[10px] text-gray-400 block font-mono">
                            Telegram ID: {r.referee_telegram_id} • Joined: {new Date(r.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <span className="font-bold font-mono text-nova-gold">
                          +{r.reward.toLocaleString()} NOVA
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* TAB 5: TASKS */}
              {activeTab === 'tasks' && (
                <div className="space-y-2">
                  {detail.website_tasks.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No completed website tasks.</p>
                  ) : (
                    detail.website_tasks.map((t, idx) => (
                      <div key={idx} className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between text-xs">
                        <div>
                          <span className="font-bold text-white">{t.title}</span>
                          <span className="text-[10px] text-gray-500 block">
                            {t.completed_at ? new Date(t.completed_at).toLocaleString() : 'In Progress'}
                          </span>
                        </div>
                        <span className="text-emerald-400 font-bold font-mono">+{t.reward_coins} NOVA</span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* TAB 6: ADS */}
              {activeTab === 'ads' && (
                <div className="space-y-2">
                  {detail.ad_attempts.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No AdsGram attempts recorded.</p>
                  ) : (
                    detail.ad_attempts.map((a) => (
                      <div key={a.id} className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between text-xs">
                        <div>
                          <span className="font-bold text-white capitalize">{a.placement_type} Ad</span>
                          <span className="text-[10px] text-gray-500 block">
                            Step: {a.sequence_step} • {new Date(a.created_at).toLocaleString()}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          a.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {a.status.toUpperCase()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* TAB 7: STARS ORDERS */}
              {activeTab === 'stars' && (
                <div className="space-y-2">
                  {detail.stars_orders.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No Stars purchase orders.</p>
                  ) : (
                    detail.stars_orders.map((s) => (
                      <div key={s.id} className="p-3 rounded-xl bg-space-900 border border-white/5 flex items-center justify-between text-xs">
                        <div>
                          <div className="flex items-center space-x-1.5">
                            <Star className="w-3.5 h-3.5 fill-nova-gold text-nova-gold" />
                            <span className="font-bold text-white">{s.stars_amount} Stars ({s.units_count}x Rig)</span>
                          </div>
                          <span className="text-[10px] text-gray-500 block font-mono mt-0.5">
                            Order: {s.id} • Charge: {s.telegram_payment_charge_id || 'Pending'}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          s.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {s.status.toUpperCase()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* TAB 8: AUDIT LOGS */}
              {activeTab === 'audit' && (
                <div className="space-y-2">
                  {detail.audit_logs.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No administrative audit logs for this user.</p>
                  ) : (
                    detail.audit_logs.map((al) => (
                      <div key={al.id} className="p-3 rounded-xl bg-space-900 border border-white/5 text-xs space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-purple-300 font-mono">{al.action}</span>
                          <span className="text-[10px] text-gray-500">{new Date(al.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-[11px] text-gray-400 font-mono break-all">{al.details}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
